import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTrash,
  faCog,
  faSyncAlt,
  faSearch,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
  faEdit,
  faHashtag
} from "@fortawesome/free-solid-svg-icons";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";

const CorporateDataTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    corporateCode: "",
    corporateName: "",
    tinNumber: "",
    billingAddress: "",
    status_flag: "",
    corpPrefix: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "corporateCode",
    direction: "asc",
  });

  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(
    AdminAccessAlertContext
  );

  const statusMap = {
    1: { name: "Active", color: "bg-green-100 text-green-800" },
    2: { name: "Terminated/Cancelled", color: "bg-red-100 text-red-800" },
    3: { name: "Suspended", color: "bg-yellow-100 text-yellow-800" },
    4: { name: "Expired", color: "bg-gray-100 text-gray-800" },
  };

  const columns = [
    { key: "corporateCode", label: "Code", sortable: true },
    { key: "corporateName", label: "Name", sortable: true },
    { key: "corpPrefix", label: "Prefix", sortable: true },
    { key: "tinNumber", label: "TIN", sortable: true },
    { key: "billingAddress", label: "Address", sortable: true },
    { key: "status_flag", label: "Status", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  useEffect(() => {
    fetchData();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/corporate/data`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setData(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      Swal.fire("Error!", "Failed to fetch data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2.5 py-1 rounded-md text-sm font-medium transition-colors ${
            currentPage === i
              ? "bg-blue-600 text-white"
              : "text-gray-600 hover:bg-blue-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const handleCorporateDetails = (id) => {
    navigate(`/corporate-settings/details/${id}`);
  };

  const handleCorporateUpdateCorpPrefix = (id) => {
    navigate(`/corporate-settings/update-corp-prefix/${id}`);
  };

  const handleCorporateSetup = (corp_code) => {
    navigate(`/corporate-settings/setup/${corp_code}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to delete this corporate record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}/corporate-settings/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        });
        Swal.fire("Deleted!", "The record has been deleted.", "success");
        fetchData();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete the record.", "error");
      }
    }
  };

  const LoadingSkeleton = ({ rows = 5, columns = 5 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b border-gray-100">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="px-4 py-3">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans">
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <FontAwesomeIcon icon={faCog} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Corporate Management
            </h1>
            <p className="text-xs text-gray-500">
              Manage records and billing fees
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Top Controls */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
              <button
                onClick={fetchData}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faSyncAlt} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-3 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="flex gap-3 min-w-max">
              {columns.map(
                (col) =>
                  col.key !== "actions" && (
                    <div key={col.key} className="flex-1 min-w-[120px]">
                      {col.key === "status_flag" ? (
                        <select
                          value={filters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                        >
                          <option value="">All Statuses</option>
                          {Object.entries(statusMap).map(([code, { name }]) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder={col.label}
                          value={filters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                        />
                      )}
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-2 text-left text-xs font-medium ${
                        column.sortable
                          ? "cursor-pointer hover:bg-gray-100"
                          : ""
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-[8px]">
                            <span
                              className={`${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "asc"
                                  ? "text-blue-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "desc"
                                  ? "text-blue-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton
                    rows={itemsPerPage}
                    columns={columns.length}
                  />
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-6 text-center text-gray-500 text-sm"
                    >
                      No data available.
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-700">
                        {row.corporateCode}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.corporateName}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.corpPrefix}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.tinNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.billingAddress}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            statusMap[row.status_flag]?.color ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {statusMap[row.status_flag]?.name || "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              handleCorporateUpdateCorpPrefix(row.corporateCode)
                            }
                            className="px-2 py-1 text-xs text-white bg-teal-500 rounded hover:bg-teal-600 transition-colors"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Update Corp Prefix
                          </button>

                          <button
                            onClick={() =>
                              handleCorporateDetails(row.corporateCode)
                            }
                            className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Details
                          </button>
                          <button
                            onClick={() =>
                              handleCorporateSetup(row.corporateCode)
                            }
                            className="px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
                          >
                            <FontAwesomeIcon icon={faCog} className="mr-1" />
                            Setup
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-t border-gray-100">
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors mb-3 sm:mb-0"
            >
              {[5, 10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val} per page
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-600 rounded hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-600 rounded hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-600 rounded hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-600 rounded hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateDataTable;
